import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@staff-app/ui';
import type { IntlAllRequest } from '@staff-app/shared-types';
import { CountryCombobox } from '@/features/optihub/quote/components/CountryCombobox';
import { isKnownCountry, normalizeCountry } from '@/features/optihub/quote/countries';
import { useUpdateIntlAllShippingAddress } from '../hooks/useIntlAllRequests';

type Draft = {
  shipToName: string;
  shipToLine1: string;
  shipToLine2: string;
  shipToCity: string;
  shipToRegion: string;
  shipToPostalCode: string;
  shipToCountry: string;
  shipToPhone: string;
};

const fromRequest = (r: IntlAllRequest): Draft => ({
  shipToName: r.shipToName ?? '',
  shipToLine1: r.shipToLine1 ?? '',
  shipToLine2: r.shipToLine2 ?? '',
  shipToCity: r.shipToCity ?? '',
  shipToRegion: r.shipToRegion ?? '',
  shipToPostalCode: r.shipToPostalCode ?? '',
  shipToCountry: r.shipToCountry ?? '',
  shipToPhone: r.shipToPhone ?? '',
});

/**
 * Correct the shipping address — root, support or fulfilment, until the request is routed (Paul,
 * 2026-10-28). The whole address is saved together, prefilled with what is there now.
 */
export function AddressDialog({
  request,
  open,
  onOpenChange,
}: {
  request: IntlAllRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => fromRequest(request));
  const [save, { loading, error, reset }] = useUpdateIntlAllShippingAddress();
  useEffect(() => {
    if (open) {
      setDraft(fromRequest(request));
      reset();
    }
    // Re-seed each time it opens, from the row as it is then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k: keyof Draft) => (e: { target: { value: string } }) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const countryOk = isKnownCountry(draft.shipToCountry);
  const valid = Boolean(draft.shipToName.trim() && draft.shipToLine1.trim() && draft.shipToPhone.trim() && countryOk);

  const submit = async () => {
    if (!valid || loading) return;
    const opt = (v: string) => v.trim() || undefined;
    try {
      await save({
        variables: {
          input: {
            requestId: request.id,
            shipToName: draft.shipToName.trim(),
            shipToLine1: draft.shipToLine1.trim(),
            shipToLine2: opt(draft.shipToLine2),
            shipToCity: opt(draft.shipToCity),
            shipToRegion: opt(draft.shipToRegion),
            shipToPostalCode: opt(draft.shipToPostalCode),
            shipToCountry: normalizeCountry(draft.shipToCountry) ?? draft.shipToCountry,
            shipToPhone: draft.shipToPhone.trim(),
          },
        },
      });
      onOpenChange(false);
    } catch {
      // Shown from `error` below.
    }
  };

  const field = (label: string, k: keyof Draft, required = false) => (
    <div>
      <Label className="text-xs font-medium text-muted-foreground">{required ? `${label} *` : label}</Label>
      <Input className="mt-1" value={draft[k]} onChange={set(k)} disabled={loading} data-testid={`air-address-${k}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="air-address-dialog">
        <DialogHeader>
          <DialogTitle>{`Shipping address — ${request.requestNo}`}</DialogTitle>
          <DialogDescription>
            {'Can be changed until fulfilment routes the request. The change is noted on the request.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">{field('Recipient', 'shipToName', true)}</div>
          <div className="sm:col-span-2">{field('Address line 1', 'shipToLine1', true)}</div>
          <div className="sm:col-span-2">{field('Address line 2', 'shipToLine2')}</div>
          {field('City', 'shipToCity')}
          {field('State / region', 'shipToRegion')}
          {field('Postal code', 'shipToPostalCode')}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">{'Country *'}</Label>
            <div className="mt-1">
              <CountryCombobox
                value={draft.shipToCountry}
                onChange={(v) => setDraft((d) => ({ ...d, shipToCountry: v }))}
                testId="air-address-country"
              />
            </div>
          </div>
          <div className="sm:col-span-2">{field('Phone', 'shipToPhone', true)}</div>
        </div>
        {error ? (
          <p className="text-sm text-destructive" data-testid="air-address-error">
            {error.message}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            {'Cancel'}
          </Button>
          <Button onClick={submit} disabled={!valid || loading} data-testid="air-address-save">
            {loading ? 'Saving…' : 'Save address'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
